import React, { forwardRef } from "react";

import { cn } from "src/shadcn/lib/utils";
import {
  Empty as PrimitiveEmpty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
  EmptyMedia,
} from "src/primitives/Empty";
import { Button } from "src/components/Button";
import { Typography } from "src/components/Typography";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "src/primitives/Tooltip";
import { renderIcon, type IconProp } from "src/shared/renderIcon";

export interface EmptyProps extends React.ComponentProps<"div"> {
  /** Image URL or JSX for the media area. */
  image?: string | React.ReactNode;
  /** Icon for the media area (rendered inside EmptyMedia with variant="icon"). */
  icon?: IconProp;
  /** Empty state title. */
  title?: string;
  /** Description text or JSX. */
  description?: React.ReactNode;
  /** Additional help text below the description. */
  helpText?: React.ReactNode;
  /** Props forwarded to the primary action Button. */
  primaryButtonProps?: React.ComponentProps<typeof Button>;
  /** Props forwarded to the secondary action Button. */
  secondaryButtonProps?: React.ComponentProps<typeof Button>;
  /** Text displayed between the primary and secondary buttons (e.g., "or"). */
  buttonSeparatorText?: string;
  /** Show tooltip on disabled buttons using their label as content. */
  showTooltipWhenButtonDisabled?: boolean;
  /** Additional CSS class names. */
  className?: string;
  /** Class applied to inner slots (header/content); overrides default max-width if a `max-w-*` token is provided. */
  innerClassName?: string;
}

function MaybeTooltipButton({
  buttonProps,
  showTooltipWhenDisabled,
  defaultVariant,
}: {
  buttonProps: React.ComponentProps<typeof Button>;
  showTooltipWhenDisabled: boolean;
  defaultVariant: "default" | "outline";
}) {
  const button = <Button variant={defaultVariant} {...buttonProps} />;

  if (showTooltipWhenDisabled && buttonProps.disabled && buttonProps.label) {
    return (
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">{button}</span>
          </TooltipTrigger>
          <TooltipContent>{buttonProps.label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
}

const Empty = forwardRef<HTMLDivElement, EmptyProps>(
  (
    {
      image,
      icon,
      title,
      description,
      helpText,
      primaryButtonProps,
      secondaryButtonProps,
      buttonSeparatorText,
      showTooltipWhenButtonDisabled = false,
      className,
      ...otherProps
    },
    ref
  ) => {
    const hasMedia = image || icon;
    const hasButtons = primaryButtonProps || secondaryButtonProps;

    return (
      <PrimitiveEmpty ref={ref} className={cn(className)} {...otherProps}>
        <EmptyHeader>
          {hasMedia && (
            <>
              {image && (
                <EmptyMedia>
                  {typeof image === "string" ? (
                    <img src={image} alt={title ?? ""} />
                  ) : (
                    image
                  )}
                </EmptyMedia>
              )}
              {!image && icon && (
                <EmptyMedia variant="icon">{renderIcon(icon)}</EmptyMedia>
              )}
            </>
          )}
          {title && (
            <Typography variant="h4" asChild>
              <EmptyTitle>{title}</EmptyTitle>
            </Typography>
          )}
          {description && (
            <Typography variant="body3" color="muted" asChild>
              <EmptyDescription>{description}</EmptyDescription>
            </Typography>
          )}
          {helpText && (
            <Typography variant="body3" color="muted" asChild>
              <p>{helpText}</p>
            </Typography>
          )}
        </EmptyHeader>

        {hasButtons && (
          <EmptyContent>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {primaryButtonProps && (
                <MaybeTooltipButton
                  buttonProps={primaryButtonProps}
                  showTooltipWhenDisabled={showTooltipWhenButtonDisabled}
                  defaultVariant="default"
                />
              )}
              {buttonSeparatorText &&
                primaryButtonProps &&
                secondaryButtonProps && (
                  <Typography variant="body3" color="muted" asChild>
                    <span>{buttonSeparatorText}</span>
                  </Typography>
                )}
              {secondaryButtonProps && (
                <MaybeTooltipButton
                  buttonProps={secondaryButtonProps}
                  showTooltipWhenDisabled={showTooltipWhenButtonDisabled}
                  defaultVariant="outline"
                />
              )}
            </div>
          </EmptyContent>
        )}
      </PrimitiveEmpty>
    );
  }
);

Empty.displayName = "Empty";

export { Empty };
